<template>

  <PageHeader
    title="Page Configuration Management"
    :breadcrumbs="[{ label: 'Admin Console' }]"
  >
    <template #actions>
      <div class="ml-auto flex gap-2">
        <button
          @click="showCreateModal = true"
          class="btn-primary"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
          Create New Page Configuration
        </button>
      </div>
    </template>
  </PageHeader>

  <div class="p-4">

    <!-- Page Configurations List -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Page Name
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Identifier
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sections
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Route
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="pageConfig in pageConfigurations" :key="pageConfig.id">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ pageConfig.display_name }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span class="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                {{ pageConfig.page_type }}
              </span>
            </td>
            <td class="px-6 py-4 text-sm text-gray-500 font-mono">
              {{ pageConfig.page_identifier }}
            </td>
            <td class="px-6 py-4 text-sm text-gray-500">
              {{ getSectionCount(pageConfig) }} sections
            </td>
            <td class="px-6 py-4 text-sm text-gray-500 font-mono">
              {{ pageConfig.route_path || '—' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                :class="pageConfig.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ pageConfig.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button
                @click="editPageConfig(pageConfig)"
                class="text-primary-600 hover:text-primary-900 mr-3"
              >
                Configure
              </button>
              <button
                @click="deletePageConfig(pageConfig)"
                class="text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Edit/Create Modal -->
    <div
      v-if="showEditModal || showCreateModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeModal"
    >
      <div class="relative top-10 mx-auto p-5 w-full max-w-6xl">
        <div class="bg-white rounded-lg shadow-xl max-h-[85vh] overflow-hidden flex flex-col">
          <!-- Modal Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900">
              {{ editingPageConfig ? 'Edit Page Configuration' : 'Create Page Configuration' }}
            </h3>
            <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="h-6 w-6" />
            </button>
          </div>

          <!-- Modal Body -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- Basic Information -->
            <div class="mb-6">
              <h4 class="text-sm font-semibold text-gray-700 mb-3">Basic Information</h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    v-model="formData.display_name"
                    placeholder="e.g., User Overview"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Page Identifier</label>
                  <input
                    v-model="formData.page_identifier"
                    placeholder="e.g., user_overview"
                    :disabled="!!editingPageConfig"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Page Type</label>
                  <select
                    v-model="formData.page_type"
                    :disabled="!pageTypes.length"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option v-if="!pageTypes.length" disabled value="">No page types available</option>
                    <option v-for="pt in pageTypes" :key="pt.value" :value="pt.value">{{ pt.label }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Route Path</label>
                  <input
                    v-model="formData.route_path"
                    placeholder="e.g., /user"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    v-model="formData.description"
                    placeholder="Brief description of this page"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                    rows="2"
                  />
                </div>
                <div class="col-span-2 -mt-2">
                  <button
                    @click="editingPageSettings = !editingPageSettings"
                    class="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Settings
                  </button>
                  <div v-if="editingPageSettings" class="mt-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Page Settings (JSON)</label>
                    <textarea
                      v-model="layoutConfigJson"
                      placeholder="{}"
                      rows="4"
                      spellcheck="false"
                      class="w-full px-2 py-2 font-mono text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Page-level config (everything except <code class="bg-gray-100 px-1 rounded">sections</code>, which is edited below). Known keys: <code class="bg-gray-100 px-1 rounded">defaultYear</code> (number, e.g. 2025 — applies to Insights pages), <code class="bg-gray-100 px-1 rounded">hide_chrome</code> (boolean), <code class="bg-gray-100 px-1 rounded">hide_year_filter</code> (boolean).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Page Sections -->
            <div class="mb-6">
              <div class="flex justify-between items-center mb-3">
                <h4 class="text-sm font-semibold text-gray-700">Page Sections</h4>
                <div class="flex items-center gap-2">
                  <button
                    v-if="supportsTabs"
                    @click="addTab"
                    class="btn-secondary-sm"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    Add Tab
                  </button>
                  <button
                    v-if="!supportsTabs"
                    @click="addSection('Overview')"
                    class="btn-primary-sm"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    Add Section
                  </button>
                </div>
              </div>

              <div class="space-y-4">
                <div
                  v-for="group in groupedSections"
                  :key="group.tab"
                  :class="[
                    supportsTabs ? 'border rounded-lg overflow-hidden transition-colors' : '',
                    supportsTabs && dragOverTab === group.tab ? 'border-blue-400 ring-1 ring-blue-200' : supportsTabs ? 'border-gray-200' : ''
                  ]"
                >
                  <!-- Tab Group Header (dashboard only) -->
                  <div v-if="supportsTabs" class="bg-gray-100 px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
                    <div class="flex items-center gap-2">
                      <font-awesome-icon :icon="['fas', 'layer-group']" class="text-xs text-gray-400" />
                      <span v-if="editingTabName === group.tab">
                        <input
                          ref="tabNameInput"
                          :value="group.tab"
                          @keydown.enter="finishTabRename(group.tab, $event.target.value)"
                          @keydown.escape="editingTabName = null"
                          @blur="finishTabRename(group.tab, $event.target.value)"
                          class="px-1.5 py-0.5 text-sm font-medium border border-blue-400 rounded bg-white w-40"
                        />
                      </span>
                      <span v-else class="text-sm font-medium text-gray-700">
                        {{ group.tab }}
                      </span>
                      <span class="text-xs text-gray-400">({{ group.sections.length }})</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        v-if="group.tab !== 'Overview'"
                        @click="editingTabName = group.tab"
                        class="btn-icon text-xs"
                        title="Rename tab"
                      >
                        <font-awesome-icon :icon="['fas', 'pen']" />
                      </button>
                      <button
                        v-if="group.tab !== 'Overview' && group.sections.length === 0"
                        @click="removeTab(group.tab)"
                        class="btn-icon text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Remove empty tab"
                      >
                        <font-awesome-icon :icon="['fas', 'trash']" />
                      </button>
                      <button
                        @click="addSection(group.tab)"
                        class="btn-primary-sm"
                      >
                        <font-awesome-icon :icon="['fas', 'plus']" />
                        Add Section
                      </button>
                    </div>
                  </div>

                  <!-- Sections within Tab -->
                  <div
                    :class="[supportsTabs ? 'p-3' : '', 'space-y-3', { 'min-h-16': supportsTabs && draggedIndex !== null }]"
                    v-if="group.sections.length"
                    @dragover.prevent
                    @dragenter.self="dragOverTab = group.tab"
                    @dragleave.self="dragOverTab = null"
                    @drop.self="onDropToTab(group.tab)"
                  >
                    <div
                      v-for="{ section, index } in group.sections"
                      :key="index"
                      :draggable="true"
                      @dragstart="onDragStart(index)"
                      @dragover.prevent
                      @drop.stop="onDropToSection(index, group.tab)"
                      @dragenter="onDragEnter(index); dragOverTab = group.tab"
                      @dragleave="onDragLeave"
                      :class="[
                        'p-4 border rounded-lg bg-gray-50 cursor-move transition-all',
                        dragOverIndex === index ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      ]"
                    >
                      <div class="flex items-start gap-4">
                        <!-- Drag Handle + Order Controls -->
                        <div class="flex flex-col items-center gap-1 pt-6">
                          <div class="text-gray-400 cursor-grab active:cursor-grabbing">
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                            </svg>
                          </div>
                          <button
                            @click="moveSectionUp(index)"
                            :disabled="index === 0"
                            class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <font-awesome-icon :icon="['fas', 'arrow-up']" class="text-xs" />
                          </button>
                          <span class="text-xs text-gray-500 font-medium">{{ index + 1 }}</span>
                          <button
                            @click="moveSectionDown(index)"
                            :disabled="index === formData.layout_config.sections.length - 1"
                            class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <font-awesome-icon :icon="['fas', 'arrow-down']" class="text-xs" />
                          </button>
                        </div>

                        <!-- Section Details -->
                        <div class="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <label class="block text-xs font-medium text-gray-600 mb-1">Section ID</label>
                            <input
                              v-model="section.id"
                              placeholder="e.g., user_info"
                              class="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                          <div>
                            <label class="block text-xs font-medium text-gray-600 mb-1">Title</label>
                            <input
                              v-model="section.title"
                              placeholder="e.g., User Information"
                              class="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                          <div>
                            <label class="block text-xs font-medium text-gray-600 mb-1">Component</label>
                            <select
                              v-model="section.component"
                              @change="onComponentChange(section)"
                              class="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                              <optgroup v-for="(components, category) in componentsByCategory" :key="category" :label="category">
                                <option
                                  v-for="comp in components"
                                  :key="comp.id"
                                  :value="comp.id"
                                >
                                  {{ comp.displayName }}
                                </option>
                              </optgroup>
                            </select>
                          </div>
                          <div class="flex items-center gap-4 col-span-3">
                            <label class="flex items-center text-xs text-gray-600">
                              <input
                                v-model="section.visible"
                                type="checkbox"
                                class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-1"
                              />
                              Visible by default
                            </label>
                            <div class="flex items-center gap-3 px-2.5 py-1 rounded border border-gray-200">
                              <label class="flex items-center text-xs text-gray-600">
                                <input
                                  v-model="section.superuser_only"
                                  type="checkbox"
                                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-1"
                                />
                                Superuser only
                              </label>
                              <div class="flex items-center gap-1.5">
                                <label class="text-xs text-gray-600 shrink-0">Gated by Feature</label>
                                <select
                                  v-model="section.required_feature"
                                  :disabled="section.superuser_only"
                                  class="px-2 py-0.5 text-xs border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                  <option value="">None</option>
                                  <optgroup v-for="group in features" :key="group.category" :label="group.category_name">
                                    <option v-for="feature in group.features" :key="feature.id" :value="feature.id">
                                      {{ feature.name }}
                                    </option>
                                  </optgroup>
                                </select>
                              </div>
                            </div>
                            <div class="ml-auto flex items-center">
                              <button
                                @click="editSectionSettings(index)"
                                class="text-xs text-blue-600 hover:text-blue-800 mr-3"
                              >
                                Settings
                              </button>
                              <button
                                @click="removeSection(index)"
                                class="text-xs text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Section Settings (collapsed) -->
                      <div v-if="editingSectionIndex === index" class="mt-3 pt-3 border-t border-gray-300">
                        <label class="block text-xs font-medium text-gray-600 mb-1">Component Settings (JSON)</label>
                        <textarea
                          v-model="section.settingsJson"
                          @input="updateSectionSettings(index, $event.target.value)"
                          placeholder='{"sql_filters": {"status": "active"}}'
                          class="w-full px-2 py-2 text-xs font-mono border border-gray-300 rounded"
                          rows="4"
                        />
                        <button
                          @click="editingSectionIndex = null"
                          class="mt-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Empty tab drop zone (dashboard only) -->
                  <div
                    v-else-if="supportsTabs"
                    class="px-4 py-6 text-center text-xs text-gray-400"
                    @dragover.prevent
                    @dragenter="dragOverTab = group.tab"
                    @dragleave="dragOverTab = null"
                    @drop="onDropToTab(group.tab)"
                  >
                    {{ draggedIndex !== null ? 'Drop here to move to this tab' : 'No sections in this tab yet' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="savePageConfig"
              class="btn-primary-sm"
            >
              <font-awesome-icon :icon="['fas', 'save']" />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faTimes, faPlus, faSave, faArrowUp, faArrowDown, faPen, faTrash, faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import { getAllComponents, getComponentsByCategory, getDefaultSettings } from '@/config/configurableComponentRegistry'
import { mapGetters } from 'vuex'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'

library.add(faTimes, faPlus, faSave, faArrowUp, faArrowDown, faPen, faTrash, faLayerGroup)

export default {
  name: 'PageConfigurationAdmin',
  components: {
    FontAwesomeIcon,
    PageHeader
  },
  data() {
    return {
      pageConfigurations: [],
      pageTypes: [],
      showCreateModal: false,
      showEditModal: false,
      editingPageConfig: null,
      editingSectionIndex: null,
      draggedIndex: null,
      dragOverIndex: null,
      formData: {
        display_name: '',
        page_identifier: '',
        page_type: 'dashboard',
        description: '',
        route_path: '',
        is_active: true,
        layout_config: {
          sections: []
        }
      },
      // Mirror of the non-section keys on layout_config for the page-level
      // JSON editor. Kept as a string so partial / invalid edits don't blow up
      // the live formData. Parsed only at save time.
      layoutConfigJson: '{}',
      editingPageSettings: false,
      editingTabName: null,
      emptyTabs: [],
      dragOverTab: null,
    }
  },
  computed: {
    ...mapGetters('featureConfig', ['features']),
    availableComponents() {
      return getAllComponents()
    },
    componentsByCategory() {
      return getComponentsByCategory()
    },
    supportsTabs() {
      const tabTypes = ['dashboard', 'overview_list']
      return tabTypes.includes(this.formData.page_type)
    },
    existingTabs() {
      const tabs = new Set(['Overview'])
      for (const section of this.formData.layout_config.sections) {
        tabs.add(section.tab || 'Overview')
      }
      for (const tab of this.emptyTabs) {
        tabs.add(tab)
      }
      return [...tabs]
    },
    groupedSections() {
      const groups = []
      const seen = new Set()
      for (const tab of this.existingTabs) {
        if (seen.has(tab)) continue
        seen.add(tab)
        const sections = []
        this.formData.layout_config.sections.forEach((section, index) => {
          if ((section.tab || 'Overview') === tab) {
            sections.push({ section, index })
          }
        })
        groups.push({ tab, sections })
      }
      return groups
    }
  },
  mounted() {
    this.fetchPageConfigurations()
    this.fetchPageTypes()
    this.$store.dispatch('featureConfig/fetchConfig')
  },
  methods: {
    // Populate the JSON textarea from formData.layout_config on edit open
    // (sections live in their own editor below).
    syncLayoutConfigJsonFromFormData() {
      // eslint-disable-next-line no-unused-vars
      const { sections, ...rest } = this.formData.layout_config || {};
      this.layoutConfigJson = JSON.stringify(rest, null, 2);
    },
    updateSectionSettings(index, jsonString) {
      try {
        const settings = JSON.parse(jsonString)
        this.formData.layout_config.sections[index].settings = settings
      } catch (e) {
        // Invalid JSON: keep the last valid parsed settings in place.
        // Save uses section.settings (the parsed object), so editor text
        // that never parses is silently dropped — by design (per earlier
        // decision to skip an in-editor validator).
      }
    },
    async fetchPageConfigurations() {
      try {
        const response = await axios.get('/api/page-configurations/')
        this.pageConfigurations = response.data
      } catch (error) {
        console.error('Error fetching page configurations:', error)
      }
    },

    async fetchPageTypes() {
      try {
        const response = await axios.get('/api/page-configurations/page-types/')
        this.pageTypes = response.data
      } catch (error) {
        console.error('Error fetching page types:', error)
      }
    },

    getSectionCount(pageConfig) {
      return pageConfig.layout_config?.sections?.length || 0
    },

    editPageConfig(pageConfig) {
      this.editingPageConfig = pageConfig
      this.formData = {
        display_name: pageConfig.display_name,
        page_identifier: pageConfig.page_identifier,
        page_type: pageConfig.page_type,
        description: pageConfig.description || '',
        route_path: pageConfig.route_path || '',
        is_active: pageConfig.is_active,
        layout_config: JSON.parse(JSON.stringify(pageConfig.layout_config || { sections: [] }))
      }

      // Add settingsJson annotation for editing
      this.formData.layout_config.sections.forEach(section => {
        section.settingsJson = JSON.stringify(section.settings || {}, null, 2)
        if (!section.required_feature) section.required_feature = ''
        if (section.superuser_only === undefined) section.superuser_only = false
      })
      this.syncLayoutConfigJsonFromFormData()

      this.showEditModal = true
    },

    addSection(tab) {
      const defaultComponent = this.availableComponents[0]?.id || 'Action_Table'
      const defaultSettings = getDefaultSettings(defaultComponent)
      const effectiveTab = tab || 'Overview'

      this.formData.layout_config.sections.push({
        id: '',
        component: defaultComponent,
        title: '',
        tab: effectiveTab === 'Overview' ? '' : effectiveTab,
        order: this.formData.layout_config.sections.length,
        visible: true,
        superuser_only: false,
        required_feature: '',
        settings: defaultSettings,
        settingsJson: JSON.stringify(defaultSettings, null, 2)
      })
    },

    addTab() {
      const name = prompt('Tab name:')
      if (!name || !name.trim()) return
      const trimmed = name.trim()
      if (this.existingTabs.includes(trimmed)) return
      this.emptyTabs.push(trimmed)
    },

    removeTab(tab) {
      this.emptyTabs = this.emptyTabs.filter(t => t !== tab)
    },

    finishTabRename(oldName, newName) {
      this.editingTabName = null
      const trimmed = (newName || '').trim()
      if (!trimmed || trimmed === oldName) return
      if (this.existingTabs.includes(trimmed)) return
      for (const section of this.formData.layout_config.sections) {
        if ((section.tab || 'Overview') === oldName) {
          section.tab = trimmed === 'Overview' ? '' : trimmed
        }
      }
      const idx = this.emptyTabs.indexOf(oldName)
      if (idx !== -1) this.emptyTabs[idx] = trimmed
    },

    onComponentChange(section) {
      // Auto-populate default settings when component changes
      const defaultSettings = getDefaultSettings(section.component)
      section.settings = defaultSettings
      section.settingsJson = JSON.stringify(defaultSettings, null, 2)
    },

    removeSection(index) {
      this.formData.layout_config.sections.splice(index, 1)
    },

    editSectionSettings(index) {
      this.editingSectionIndex = this.editingSectionIndex === index ? null : index
    },


    async savePageConfig() {
      // Parse the page-level JSON box at save time. Invalid JSON = abort with
      // a clear alert. Empty input = no page-level settings beyond sections.
      let pageLevelSettings = {}
      const trimmed = String(this.layoutConfigJson || '').trim()
      if (trimmed !== '') {
        try {
          const parsed = JSON.parse(trimmed)
          if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            alert('Page Settings must be a JSON object.')
            return
          }
          pageLevelSettings = parsed
        } catch (e) {
          alert(`Page Settings JSON is invalid: ${e.message}`)
          return
        }
      }
      // Sections in pageLevelSettings (if user pasted them in) are ignored;
      // sections come from the dedicated editor below.
      // eslint-disable-next-line no-unused-vars
      const { sections: _drop, ...pageLevelRest } = pageLevelSettings

      try {
        const dataToSave = {
          ...this.formData,
          layout_config: {
            ...pageLevelRest,
            sections: this.formData.layout_config.sections.map(section => {
              const out = {
                id: section.id,
                component: section.component,
                title: section.title,
                order: section.order,
                visible: section.visible,
                settings: section.settings
              }
              if (section.tab) out.tab = section.tab
              if (section.superuser_only) out.superuser_only = true
              if (section.required_feature) out.required_feature = section.required_feature
              return out
            })
          }
        }

        if (this.editingPageConfig) {
          await axios.patch(`/api/page-configurations/${this.editingPageConfig.id}/`, dataToSave)
        } else {
          await axios.post('/api/page-configurations/', dataToSave)
        }

        await this.fetchPageConfigurations()
        this.closeModal()
      } catch (error) {
        console.error('Error saving page configuration:', error)
        alert('Failed to save page configuration')
      }
    },

    async deletePageConfig(pageConfig) {
      if (!confirm(`Delete page configuration "${pageConfig.display_name}"?`)) return

      try {
        await axios.delete(`/api/page-configurations/${pageConfig.id}/`)
        await this.fetchPageConfigurations()
      } catch (error) {
        console.error('Error deleting page configuration:', error)
        alert('Failed to delete page configuration')
      }
    },

    closeModal() {
      this.showEditModal = false
      this.showCreateModal = false
      this.editingPageConfig = null
      this.editingSectionIndex = null
      this.formData = {
        display_name: '',
        page_identifier: '',
        page_type: 'dashboard',
        description: '',
        route_path: '',
        is_active: true,
        layout_config: {
          sections: []
        }
      }
      this.layoutConfigJson = '{}'
      this.editingPageSettings = false
      this.editingTabName = null
      this.emptyTabs = []
      this.dragOverTab = null
    },

    // Drag and drop methods
    onDragStart(index) {
      this.draggedIndex = index
    },

    onDragEnter(index) {
      this.dragOverIndex = index
    },

    onDragLeave() {
      this.dragOverIndex = null
    },

    clearDragState() {
      this.draggedIndex = null
      this.dragOverIndex = null
      this.dragOverTab = null
    },

    onDropToSection(targetIndex, targetTab) {
      if (this.draggedIndex === null) { this.clearDragState(); return }

      const sections = this.formData.layout_config.sections
      const draggedSection = sections[this.draggedIndex]

      if (this.draggedIndex === targetIndex) { this.clearDragState(); return }

      draggedSection.tab = targetTab === 'Overview' ? '' : targetTab
      sections.splice(this.draggedIndex, 1)
      const insertAt = targetIndex > this.draggedIndex ? targetIndex - 1 : targetIndex
      sections.splice(insertAt, 0, draggedSection)
      sections.forEach((s, idx) => { s.order = idx })
      this.clearDragState()
    },

    onDropToTab(targetTab) {
      if (this.draggedIndex === null) { this.clearDragState(); return }

      const sections = this.formData.layout_config.sections
      const draggedSection = sections[this.draggedIndex]

      draggedSection.tab = targetTab === 'Overview' ? '' : targetTab
      sections.splice(this.draggedIndex, 1)
      sections.push(draggedSection)
      sections.forEach((s, idx) => { s.order = idx })
      this.clearDragState()
    },

    // Arrow button methods
    moveSectionUp(index) {
      if (index === 0) return

      const sections = this.formData.layout_config.sections
      const temp = sections[index]
      sections[index] = sections[index - 1]
      sections[index - 1] = temp

      // Update order values
      sections.forEach((section, idx) => {
        section.order = idx
      })
    },

    moveSectionDown(index) {
      const sections = this.formData.layout_config.sections
      if (index === sections.length - 1) return

      const temp = sections[index]
      sections[index] = sections[index + 1]
      sections[index + 1] = temp

      // Update order values
      sections.forEach((section, idx) => {
        section.order = idx
      })
    }
  }
}
</script>

<style scoped>
/* Component-specific styles */
</style>
